/**
 * Jobs — the list.
 *
 * A table on a desk, cards on a phone. Same data, same order, no second
 * component tree to keep in step: the table is hidden below the breakpoint and
 * the cards above it.
 */
import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { marginTone, money, percent, titleCase } from "../lib/format";
import { Card, Chip, DataTable, Empty, ErrorState, Loading, PageHeader, type Tone } from "../components/ui";
import { JOB_VIEWS, jobsForView, bookingLabel } from "../lib/jobViews";
import type { JobStatus } from "../lib/types";

const STATUS_TONE: Partial<Record<JobStatus, Tone>> = {
  on_site: "info",
  in_progress: "info",
  awaiting_parts: "warn",
  awaiting_approval: "warn",
  completed: "ok",
  invoiced: "ok",
  cancelled: "neutral",
};

export default function Jobs() {
  const { can } = useAuth();
  const [filter, setFilter] = useState("open");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const { data, isPending, isFetching, error, refetch } = useQuery({
    placeholderData: keepPreviousData,
    queryKey: ["jobs", filter === "billing" ? ["billing", page, search] : "list"],
    queryFn: () => api.jobs.list(filter === "billing" ? {status: "completed", limit: 51, offset: page * 50, q: search} : {}),
  });

  const rows = isFetching ? [] : filter === "billing" ? (data ?? []).slice(0, 50) : jobsForView(data ?? [], filter, search);

  if (isPending) return <Loading what="Loading jobs" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="page">
      <PageHeader
        title="Jobs"
        actions={
          can(...OFFICE) && (
            <Link className="btn btn-primary" to="/jobs/new">
              New job
            </Link>
          )
        }
      >
        Track service calls, installations and project stages, with equipment history when you need it.
      </PageHeader>

      <div className="toolbar">
        <div className="segmented">
          {JOB_VIEWS.filter(f => f.key !== "billing" || can(...OFFICE)).map((f) => (
            <button
              key={f.key}
              type="button"
              className={filter === f.key ? "active" : ""}
              onClick={() => {setFilter(f.key); setPage(0);}}
              aria-pressed={filter === f.key}
            >
              {f.label}
            </button>
          ))}
        </div>
        <label className="search">
          <input
            type="search"
            maxLength={200}
            aria-label="Search jobs by number or title"
            value={search}
            placeholder="Job number or title"
            onChange={(e) => {setSearch(e.target.value); setPage(0);}}
          />
        </label>
      </div>

      {filter === "billing" && <Card title="Completed jobs awaiting invoice review">
        <p>Review prices, variations and existing invoices before proceeding. Opening a review does not create or send an invoice.</p>
        <p role="status">{isFetching ? "Updating results..." : "Search covers all completed jobs. Page "+(page + 1)+"."}</p>
        <div className="toolbar">
          <button className="btn" disabled={isFetching || page === 0} onClick={() => setPage(page - 1)}>Previous page</button>
          <button className="btn" disabled={isFetching || data.length <= 50} onClick={() => setPage(page + 1)}>Next page</button>
        </div>
        <div className="stack">{rows.map(job => <div key={job.id}>
          <Link to={"/jobs/"+job.id}>{job.number} · {job.title}</Link>{" "}
          <Link className="btn" to={"/invoices?job="+job.id}>Review invoice</Link>
        </div>)}</div>
      </Card>}
      {isFetching ? <p role="status">Loading matching jobs...</p> : rows.length === 0 ? (
        <Card>
          <Empty title="No jobs match">
            {filter === "billing" ? "No completed jobs match on this page. Try the previous page or clear the search." : data.length === 0
              ? "Nothing has been created yet. Accepting a quote creates jobs automatically."
              : "Try a different filter or clear the search."}
          </Empty>
        </Card>
      ) : filter === "billing" ? null : (
        <>
          <Card padded={false}>
            <div className="desk-only">
              <DataTable
                columns={[
                  { key: "no", label: "Job" },
                  { key: "title", label: "Work" },
                  { key: "type", label: "Type" },
                  { key: "booking", label: "Booking (device time)" },
                  { key: "status", label: "Status" },
                  { key: "value", label: "Value", align: "right" },
                  { key: "margin", label: "Margin", align: "right" },
                ]}
              >
                {rows.map((job) => (
                  <tr key={job.id} className="clickable">
                    <td className="mono">
                      <Link className="row-link" to={`/jobs/${job.id}`}>
                        {job.number}
                      </Link>
                    </td>
                    <td>{job.title}<div><Link className="tiny" to={`/customers/${job.customer_id}`}>Customer & bookings</Link></div></td>
                    <td>
                      <Chip tone="neutral">{titleCase(job.type)}</Chip>
                    </td>
                    <td>{bookingLabel(job)}</td>
                    <td>
                      <Chip tone={STATUS_TONE[job.status] ?? "neutral"}>{titleCase(job.status)}</Chip>
                    </td>
                    <td className="right mono">{money(job.quoted_cents || job.revenue_cents)}</td>
                    <td className={`right mono text-${marginTone(job.margin_bp)}`}>
                      {job.revenue_cents > 0 ? percent(job.margin_bp) : "—"}
                    </td>
                  </tr>
                ))}
              </DataTable>
            </div>
          </Card>

          <div className="phone-only stack">
            {rows.map((job) => (
              <Link key={job.id} to={`/jobs/${job.id}`} className="jobcard">
                <div className="jobcard-top">
                  <span className="mono jobcard-no">{job.number}</span>
                  <Chip tone={STATUS_TONE[job.status] ?? "neutral"}>{titleCase(job.status)}</Chip>
                </div>
                <span className="jobcard-title">{job.title}</span>
                <span className="tiny muted">{bookingLabel(job)} (device time)</span>
                <div className="jobcard-foot">
                  <span>{titleCase(job.type)}</span>
                  <span className="mono">{money(job.quoted_cents || job.revenue_cents)}</span>
                  {job.revenue_cents > 0 && (
                    <span className={`mono text-${marginTone(job.margin_bp)}`}>{percent(job.margin_bp)}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
